/**
 * 홈 화면 앱 — **아이폰용 아이콘·이름 교체**와 **[앱으로 설치] 단추** (v2.188.0 · 2026-09-17).
 *
 * ⭐⭐⭐**앱은 하나다.** 포털의 홈 화면 앱은 「SM 포털」 하나이고, 그 안에서 📦 재고 · 📅 일정
 *   **두 칸**을 바꿔 쓴다(`components/MobileAppTabs.tsx`).
 *
 * ⛔⛔⛔**두 번째 매니페스트를 다시 만들지 말 것.** v2.184.0~2.187.1 에 「SM 일정」을 두 번째
 *   앱으로 깔게 하려고 화면마다 매니페스트를 바꿔 끼웠는데, **안드로이드에서는 원리적으로 안 된다**:
 *   크롬은 이미 깔린 앱의 **범위가 이 주소를 덮으면** 「이미 설치됨」으로 보고
 *   `beforeinstallprompt` 를 **아예 던지지 않는다**(구글 문서 명시 · 2026-09-17 실증).
 *   「SM 포털」의 범위가 `/` 라 사이트 전체를 덮으므로, 그 앱이 깔린 폰에서는 두 번째 앱이
 *   **영영 안 깔린다.** 범위를 좁혀 나누는 길도 막혔다 — `/login`·2단계 인증·마이페이지가
 *   범위 밖이 되고, 우리 로그인 쿠키는 **브라우저를 닫으면 만료**돼서 그 길을 자주 지나간다.
 *
 * ⭐그래서 여기 남은 일은 둘뿐이다.
 *   ① **아이폰**: 사파리는 매니페스트를 안 보고 **보고 있던 주소**를 홈 화면에 넣으면서
 *      아이콘은 `apple-touch-icon`, 이름은 `apple-mobile-web-app-title` 을 본다 →
 *      일정 화면에서는 그 둘을 달력 것으로 바꿔 둔다(첫 판정은 `index.html` 머리글 스크립트가,
 *      화면 안에서 오가는 동안은 `useInstallAs` 가 맡는다).
 *   ② **안드로이드**: 브라우저가 던져 주는 설치 신호를 붙잡아 두었다가 [📲 앱으로 설치] 단추에 물린다.
 *
 * ⚠**로그인은 따로 손댈 것이 없다.** 앱이 `/m/calendar` 로 열렸는데 로그인이 풀려 있으면
 *   `ProtectedRoute` 가 가려던 곳을 기억해 두고(`state.from`), 로그인 뒤 그리로 돌려보낸다.
 * ⛔**매니페스트의 `scope` 는 `/` 여야 한다.** 좁히면 로그인이 풀린 순간 `/login` 이 범위 밖이라
 *   크롬이 **브라우저 탭으로 내보낸다.** 「다른 메뉴로 못 가게」는 scope 가 아니라
 *   **사이드바 없는 껍데기(`MobileShell`)** 가 맡는다.
 */

import { useEffect, useReducer } from 'react';

/** 아이폰 홈 화면에 들어갈 아이콘·이름 (매니페스트와 무관 — 사파리는 이 태그들만 본다) */
interface AppleLook {
  icon: string;
  title: string;
}

/** ⚠여기 값은 `index.html` 머리글 스크립트 · `public/*.png` 과 **글자 그대로** 맞아야 한다 */
const LOOKS: Record<'portal' | 'calendar', AppleLook> = {
  portal: { icon: '/apple-touch-icon.png', title: 'SM 포털' },
  calendar: { icon: '/apple-touch-icon-calendar.png', title: 'SM 일정' },
};

/** 빌드(vite-plugin-pwa)가 머리글에 넣어 주는 **유일한** 매니페스트 */
const PORTAL_MANIFEST = '/manifest.webmanifest';

function setLook(look: AppleLook): void {
  const icon = document.head.querySelector('link[rel="apple-touch-icon"]');
  if (icon) icon.setAttribute('href', look.icon);
  const title = document.head.querySelector('meta[name="apple-mobile-web-app-title"]');
  if (title) title.setAttribute('content', look.title);
}

/**
 * 이 화면에 머무는 동안 **아이폰 홈 화면에 들어갈 아이콘·이름**을 바꾼다.
 * 화면을 떠나면 포털 기본값으로 되돌린다.
 *
 * ⚠**「떠날 때 원래 값으로」가 아니라 「포털 기본값으로」 되돌린다.** 일정 주소로 **바로** 들어온
 *   경우에는 머리글 스크립트가 이미 달력 것으로 바꿔 놓았고, 그것을 「원래 값」으로 기억하면
 *   화면을 떠난 뒤에도 달력 아이콘이 남는다.
 */
export function useInstallAs(app: keyof typeof LOOKS): void {
  useEffect(() => {
    setLook(LOOKS[app]);
    return () => { setLook(LOOKS.portal); };
  }, [app]);
}

/* ────────────────────────────────────────────────────────────────────────
   [📲 앱으로 설치] 단추

   ⛔⛔**「앱 설치」와 「바로가기 추가」는 다른 것이다.** 안드로이드 크롬·삼성인터넷 메뉴에는
   둘 다 있고, **바로가기**를 고르면 그냥 **평범한 브라우저 탭**이 열린다 — 주소창도 탭 개수도
   그대로다. 사용자가 실제로 그렇게 됐다(2026-09-16 · 탭 6개가 찍힌 화면).

   → 메뉴에서 고르게 두지 말고 **화면에 단추를 둔다.** 브라우저가 「이 사이트는 앱으로 깔 수
     있다」고 판단하면 `beforeinstallprompt` 를 던지는데, 그것을 붙잡아 두었다가 단추를 누를 때
     그대로 띄운다. 이 길로 깔면 **반드시 앱(주소창 없음)** 이다.

   ⚠**이 모듈이 불려오는 시점에 곧바로 귀를 연다.** 그 사건은 페이지를 연 직후 한 번 던져지고
     끝이라, React 가 화면을 그린 뒤에 귀를 열면 이미 지나가 버린다.
     (`main.tsx` 가 이 파일을 첫 줄에서 불러온다 — 그래서 충분히 이르다.)
   ⚠아이폰(사파리)은 이 사건을 **아예 안 던진다** → 단추가 안 뜨고 글자 안내가 남는다(정상).
   ⚠**이미 앱이 깔린 폰에서도 안 던진다** → 그때도 글자 안내가 남는다. 화면은 그 두 경우를
     구분할 수 없으니, 안내 문구가 **양쪽 다 짚어 주어야** 한다.
   ──────────────────────────────────────────────────────────────────────── */

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
}

let pending: InstallPromptEvent | null = null;
/** 그 사건이 던져질 때 걸려 있던 매니페스트 주소 — 엉뚱한 앱을 깔지 않기 위한 확인용 */
let pendingFor: string | null = null;
const watchers = new Set<() => void>();
const tellWatchers = (): void => { for (const f of watchers) f(); };

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // 막지 않으면 브라우저가 제 방식대로 띄웠다 사라진다 — 우리가 단추에 물려 쓴다
    e.preventDefault();
    pending = e as InstallPromptEvent;
    pendingFor = document.head.querySelector('link[rel="manifest"]')?.getAttribute('href') ?? null;
    tellWatchers();
  });
  window.addEventListener('appinstalled', () => {
    pending = null;
    pendingFor = null;
    tellWatchers();
  });
}

/**
 * 「지금 이 화면을 앱으로 깔 수 있나」와, 깔기를 띄우는 함수.
 * `canInstall` 이 거짓이면 **단추를 감추고 글자 안내**를 남긴다 — 눌리지 않는 단추는 거짓말이다.
 */
export function useInstallPrompt(): { canInstall: boolean; install: () => void } {
  const [, bump] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    watchers.add(bump);
    return () => { watchers.delete(bump); };
  }, []);
  return {
    // ⭐매니페스트가 하나뿐이라 어긋날 일이 없지만, 확인은 남겨 둔다 —
    //   누군가 두 번째 매니페스트를 다시 붙이면 여기서 걸린다(⛔붙이지 말 것).
    canInstall: pending !== null && pendingFor === PORTAL_MANIFEST,
    install: () => {
      const e = pending;
      if (!e) return;
      // ⭐한 번 쓰면 다시 못 쓴다 — 먼저 비우고 띄운다
      pending = null;
      pendingFor = null;
      tellWatchers();
      void e.prompt();
    },
  };
}

/** 이미 홈 화면 앱으로 열린 상태인가 — 안내 문구를 또 띄우지 않으려고 본다 */
export function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true
    // 아이폰 사파리는 표준 대신 이 값을 쓴다
    || (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}
